import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { UserContext } from "../UserContext";
import Header from "../Header";
import apiClient from "../../api/client";
import "../InternetPlanMapping/index.css";

/**
 * Assign Pioneer IPTV DPO packs to an admin / operator with a price — like the internet plan
 * mapping. Prices are per 30 days and include FTA + 18% GST. A-la-carte / Broadcaster add-ons are
 * not assigned here: every account that sells a DPO gets them at the Pioneer IPTV page prices.
 */
const PioneerPlanMapping = () => {
  const { id } = useParams();
  const { user } = useContext(UserContext);
  const isAdmin = user?.role === "admin";

  const [operator, setOperator] = useState(null);
  const [packs, setPacks] = useState([]);
  const [myPrices, setMyPrices] = useState({});
  const [mapped, setMapped] = useState({}); // { pack_id: price string }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [bulkAmount, setBulkAmount] = useState("");
  const [q, setQ] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/customer/pioneeriptv/pioneer_pack_mapping.php?operator_id=${id}`);
      if (!data.success) throw new Error(data.error || "Failed to load");
      setOperator(data.operator || null);
      setPacks(data.packs || []);
      setMyPrices(data.my_prices || {});
      const map = {};
      (data.mappings || []).forEach((m) => { map[m.pack_id] = String(m.price); });
      setMapped(map);
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed to load Pioneer IPTV plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const bulk = () => (bulkAmount && parseInt(bulkAmount, 10) > 0 ? parseInt(bulkAmount, 10) : 0);
  const startPrice = (p) => {
    const base = isAdmin && myPrices[p.id] !== undefined ? Number(myPrices[p.id]) : Number(p.base_price || 0);
    return String(base + bulk());
  };

  const toggle = (p) =>
    setMapped((prev) => {
      const next = { ...prev };
      if (next[p.id] !== undefined) delete next[p.id];
      else next[p.id] = startPrice(p);
      return next;
    });

  const selectAll = () => {
    const next = {};
    packs.forEach((p) => { next[p.id] = mapped[p.id] !== undefined ? mapped[p.id] : startPrice(p); });
    setMapped(next);
  };

  const applyBulk = () => {
    const amount = parseInt(bulkAmount, 10);
    if (Number.isNaN(amount) || amount < 0) {
      toast.warn("Please enter a valid amount");
      return;
    }
    const ids = Object.keys(mapped);
    if (!ids.length) {
      toast.warn("No packs selected");
      return;
    }
    const next = { ...mapped };
    ids.forEach((pid) => {
      const p = packs.find((x) => String(x.id) === String(pid));
      if (p) next[pid] = startPrice(p);
    });
    setMapped(next);
    toast.success(`Added ₹${amount} to ${ids.length} pack(s)`);
  };

  const save = async () => {
    const mappings = Object.keys(mapped).map((pid) => ({ pack_id: Number(pid), price: mapped[pid] === "" ? null : Number(mapped[pid]) }));
    setSaving(true);
    try {
      const { data } = await apiClient.post("/customer/pioneeriptv/pioneer_pack_mapping.php", { operator_id: Number(id), mappings });
      if (!data.success) throw new Error(data.error || "Failed to save");
      toast.success(data.message || "Saved");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Server error while saving");
    } finally {
      setSaving(false);
    }
  };

  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = packs.filter((p) => !words.length || words.every((w) => p.name.toLowerCase().includes(w)));
  const count = Object.keys(mapped).length;

  return (
    <>
      <Header />
      <div className="mapping-container">
        <h1>
          Pioneer IPTV Plan Mapping{" "}
          {operator && <span>— {operator.operatorName || operator.username}</span>}{" "}
          {isAdmin && <span className="admin-badge">(Admin)</span>}
        </h1>
        <p style={{ color: "#64748b", fontSize: 13, margin: "4px 0 16px" }}>
          {isAdmin
            ? "Only the DPO packs assigned to you are listed, with your price per 30 days. A-la-carte channels and broadcaster packs don't need assigning — they come with the DPO."
            : "DPO packs, price per 30 days including FTA and 18% GST. A-la-carte channels and broadcaster packs don't need assigning — they come with the DPO at the prices on the Pioneer IPTV page."}
        </p>

        {loading ? (
          <p>Loading Pioneer IPTV plans...</p>
        ) : packs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📺</div>
            <p>
              {isAdmin
                ? "No Pioneer IPTV DPO packs are assigned to your account yet. Ask the super admin to assign them first."
                : "No Pioneer DPO packs yet. Press “Sync from Pioneer” on the Pioneer IPTV page."}
            </p>
          </div>
        ) : (
          <>
            <div className="bulk-actions">
              <div className="bulk-selection">
                <div className="bulk-btn-group">
                  <button className="bulk-btn select-all-btn" onClick={selectAll} disabled={count === packs.length}>Select All</button>
                  <button className="bulk-btn deselect-all-btn" onClick={() => { setMapped({}); setBulkAmount(""); }}>Deselect All</button>
                </div>
                <span className="selected-count">{count} of {packs.length} packs selected</span>
              </div>
              <div className="bulk-amount-section">
                <input type="text" placeholder="Search DPO…" value={q} onChange={(e) => setQ(e.target.value)} />
                {isAdmin && (
                  <>
                    <input type="number" placeholder="Bulk add amount" value={bulkAmount} onChange={(e) => setBulkAmount(e.target.value)} />
                    <button className="bulk-btn" onClick={applyBulk}>Apply to Selected</button>
                  </>
                )}
              </div>
            </div>

            <div className="mapping-table-container">
              <table className="mapping-table">
                <thead>
                  <tr>
                    <th>Assign</th>
                    <th>DPO package</th>
                    <th>Channels</th>
                    <th>{isAdmin ? "Your price" : "Base price"}</th>
                    <th>Price for this account</th>
                    <th>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((p) => {
                    const on = mapped[p.id] !== undefined;
                    const price = on ? mapped[p.id] : "";
                    const ref = isAdmin && myPrices[p.id] !== undefined ? Number(myPrices[p.id]) : Number(p.base_price || 0);
                    const diff = on && price !== "" ? Number(price) - ref : 0;
                    return (
                      <tr key={p.id} className={on ? "selected" : ""}>
                        <td><input type="checkbox" checked={on} onChange={() => toggle(p)} /></td>
                        <td><strong>{p.name}</strong><div style={{ fontSize: 11, color: "#15803d", fontWeight: 600 }}>FTA included</div></td>
                        <td title={(p.channels || []).join(", ")}>{p.channel_count === null ? "—" : p.channel_count}</td>
                        <td>₹{p.base_price ?? "—"}</td>
                        <td>
                          {on ? (
                            <input type="number" min="0" step="1" value={price} placeholder="Enter price"
                              onChange={(e) => setMapped((m) => ({ ...m, [p.id]: e.target.value }))} />
                          ) : "-"}
                        </td>
                        <td>
                          {on && price !== "" ? (
                            <span className={`price-difference ${diff >= 0 ? "positive" : "negative"}`}>{diff >= 0 ? "+" : ""}₹{diff}</span>
                          ) : "-"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mapping-actions">
              <button className="save-btn" onClick={save} disabled={saving}>
                {saving ? "Saving..." : `Save Mapping (${count})`}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default PioneerPlanMapping;
